import React from "react";
import A4PageFrame from "./A4PageFrame";
import { fmtNum, fmtPct } from "./formatters";
import { ROWS_PER_PAGE_LANDSCAPE } from "./pageMetrics";

// "Résultats par module" — landscape, TABLE ONLY.
// Pass #3: the client asked to delete the graphs here (e.g. Etang B has too many
// modules for a readable chart). Column list: Numéro / Module / Surface m² /
// <year> HT / CHF/m2 / <year> TTC / <prior year> HT / Diff. HT / Diff.% / Part %.
//
// PAGINATION added 2026-08-06. Client (Espace Tourbillon): "Resultats par module:
// missing 2nd page". Same defect as ModulesSummary — no pagination here and
// Preview.js hard-coded RESULTS_MODULE_PAGES = 1, so this building rendered ~23 of
// its 148 modules and dropped ~125, with the "Total" row pushed off the page too.
// His screenshot shows exactly 23 rows plus one clipped mid-row, which matches
// ROWS_PER_PAGE_LANDSCAPE — the same budget ResultsList uses for the other landscape
// tables. Totals render on the LAST page only; the title gains "(n/N)" only when
// there is more than one page, so single-page buildings stay byte-identical.

// Chunk module rows into page-sized groups, reserving room for the Total row on the
// last page (a completely full final chunk gets an extra page rather than clipping).
export function splitResultsPerModule(data) {
  const modules = data?.modules || [];
  const chunks = [];
  for (let i = 0; i < modules.length; i += ROWS_PER_PAGE_LANDSCAPE) {
    chunks.push(modules.slice(i, i + ROWS_PER_PAGE_LANDSCAPE));
  }
  if (!chunks.length) chunks.push([]);
  if (chunks[chunks.length - 1].length >= ROWS_PER_PAGE_LANDSCAPE) chunks.push([]);
  return chunks;
}

// Page count for Preview.js — same chunker as the render, so they cannot drift.
export function resultsPerModulePageCount(data) {
  return splitResultsPerModule(data).length;
}

const ResultsPerModule = ({
  data,
  building,
  periodLabel,
  periodYears,
  compareEnabled,
  whiteLabelling,
  logoUrl,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const totals = data?.totals || {};
  const currentYear = periodYears?.current ?? "";
  const priorYear = periodYears?.prior ?? "";

  const chunks = splitResultsPerModule(data);
  return (
    <>
      {chunks.map((chunk, pageIdx) => {
        const isLast = pageIdx === chunks.length - 1;
        return (
    <A4PageFrame
      key={pageIdx}
      building={building}
      periodLabel={periodLabel}
      sectionTitle={chunks.length > 1 ? `Résultats par module (${pageIdx + 1}/${chunks.length})` : "Résultats par module"}
      whiteLabelling={whiteLabelling}
      logoUrl={logoUrl}
      pageNumber={basePageNumber + pageIdx}
      totalPages={grandTotalPages}
      landscape
      resultsPage
    >
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 4 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #00033F" }}>
            <th style={th}>Numéro</th>
            <th style={th}>Module</th>
            <th style={thNum}>Surface m²</th>
            <th style={thNum}>{currentYear} HT</th>
            <th style={thNum}>CHF/m2</th>
            <th style={thNum}>{currentYear} TTC</th>
            {compareEnabled && <th style={thNum}>{priorYear} HT</th>}
            {compareEnabled && <th style={thNum}>Diff. HT</th>}
            {compareEnabled && <th style={thNum}>Diff. %</th>}
            <th style={thNum}>Part %</th>
          </tr>
        </thead>
        <tbody>
          {chunk.map((m) => (
            <tr key={m.numero}>
              <td style={td}>{m.numero}</td>
              <td style={td}>{m.module || ""}</td>
              <td style={tdNum}>{fmtNum(m.surface_m2)}</td>
              <td style={tdNum}>{fmtNum(m.main_ht)}</td>
              <td style={tdNum}>{fmtNum(m.chf_m2)}</td>
              <td style={tdNum}>{fmtNum(m.main_ttc)}</td>
              {compareEnabled && <td style={tdNum}>{fmtNum(m.compare_ht)}</td>}
              {compareEnabled && <td style={tdNum}>{fmtNum(m.diff)}</td>}
              {compareEnabled && <td style={tdNum}>{fmtPct(m.diff_pct)}</td>}
              <td style={tdNum}>{fmtPct(m.part_pct)}</td>
            </tr>
          ))}
          {/* Total on the LAST page only — it used to be clipped off entirely. */}
          {isLast && (
          <tr style={{ borderTop: "1px solid #00033F", fontWeight: 700 }}>
            <td style={td} />
            <td style={td}>Total</td>
            <td style={tdNum}>{fmtNum(totals.surface_m2)}</td>
            <td style={tdNum}>{fmtNum(totals.main_ht)}</td>
            <td style={tdNum} />
            <td style={tdNum}>{fmtNum(totals.main_ttc)}</td>
            {compareEnabled && <td style={tdNum}>{fmtNum(totals.compare_ht)}</td>}
            {compareEnabled && <td style={tdNum} />}
            {compareEnabled && <td style={tdNum} />}
            <td style={tdNum} />
          </tr>
          )}
        </tbody>
      </table>
    </A4PageFrame>
        );
      })}
    </>
  );
};

const th = { padding: "4px 6px", fontWeight: 600, textAlign: "left", fontSize: 11 };
const thNum = { ...th, textAlign: "right" };
const td = { padding: "5px 6px", fontSize: 10, lineHeight: "14px", borderBottom: "1px solid rgba(0,3,63,0.05)" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default ResultsPerModule;
